import { useTranslation } from 'module/hooks/use-translation';
import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { GroupedOptions } from 'module/types/filter-field/field/grouped-field';
import {
  defaultGroupedFilter,
  GroupedFilter,
} from 'module/types/filter-field/grouped-filter/filter';
import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { isFieldFilterValid } from 'module/types/filter-field/grouped-filter/utils';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { GroupedFilterAsBadges } from 'module/ui/components/molecules/field-filter/as-badges/grouped-filter-as-badges';
import { GroupedFilterDrawer } from 'module/ui/components/molecules/field-filter/drawer/grouped-filter-drawer';
import { ApiFilterTransformer } from 'module/ui/components/molecules/field-filter/filter-transformation/api-filter-transformer';
import { HeadingCompact01 } from 'module/ui/components/styles/typography';
import { faFilter } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PageType } from 'api/page-template/types';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

const Wrapper = styled.div`
  min-width: 652px;
`;

const Container = styled.div<{ $active: boolean }>`
  border: 1px solid var(--purple-40);
  border-radius: var(--border-radius-small);
  background: white;
  min-height: 72px;
  padding: var(--spacing-05);
  display: flex;
  flex-direction: column;
  &:hover {
    box-shadow: var(--shadow-02);
  }
  ${({ $active }) =>
    $active &&
    css`
      border: 2px solid var(--purple-60);
    `};
`;

const Header = styled.div`
  ${HeadingCompact01};
  color: var(--purple-60);
`;

const FilterAsBadge = styled(GroupedFilterAsBadges)``;
const FilterWrapper = styled(CenterAlignedBox)`
  padding-bottom: var(--spacing-03);
  padding-top: var(--spacing-05);
  width: fit-content;
`;

const Title = styled(CenterAlignedBox)`
  display: flex;
  align-items: center;
  gap: var(--spacing-04);
  width: fit-content;
`;

const TitleDescription = styled.div`
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-regular);
  color: var(--gray-70);
`;

const AddCondition = styled.div<{ isEdit?: boolean; $readOnly?: boolean }>`
  color: var(--purple-50);
  font-weight: var(--font-weight-regular);
  cursor: pointer;
  color: ${({ isEdit }) =>
    isEdit ? 'var(--text-tertiary)' : 'var(--purple-50)'};
  cursor: ${({ $readOnly }) => ($readOnly ? 'default' : 'pointer')};
  pointer-events: ${({ $readOnly }) => ($readOnly ? 'none' : 'auto')};
`;

const aiQueryPages = [PageType.Email, PageType.Meeting];

export const TemplateConditions = ({
  handleTemplateConditionsChange,
  apiFilter,
  groupedFields,
  groupedTransformer,
  pageType,
  readOnly = false,
}: {
  handleTemplateConditionsChange: (filter: GroupedFilter) => void;
  apiFilter: GroupedFilter | null;
  groupedFields: GroupedOptions<FilterableField>[];
  groupedTransformer?: ApiFilterTransformer<GroupedFilter>;
  pageType: PageType;
  readOnly?: boolean;
}) => {
  const [isFilterDrawerVisible, setIsFilterDrawerVisible] = useState(false);
  const openFilterDrawer = useCallback(() => {
    if (!readOnly) {
      setIsFilterDrawerVisible(true);
    }
  }, [readOnly]);
  const closeFilterDrawer = useCallback(() => {
    setIsFilterDrawerVisible(false);
  }, []);

  const [filter, setFilter] = useState<GroupedFilter>(
    apiFilter ? apiFilter : defaultGroupedFilter(FilterGroupType.Company),
  );

  const hasSomeValidFilter = useMemo(() => {
    return filter.groupCriteria.some(({ criteria }) =>
      isFieldFilterValid(criteria),
    );
  }, [filter]);

  const onDone = useCallback(
    (filter: GroupedFilter) => {
      setFilter(filter);
      closeFilterDrawer();
      handleTemplateConditionsChange(filter);
    },
    [closeFilterDrawer, handleTemplateConditionsChange],
  );

  const hasAiQuery = useMemo(() => {
    return aiQueryPages.includes(pageType);
  }, [pageType]);

  const translationPrefix = useMemo(() => {
    return `${pageType.toLowerCase()}_page_template`;
  }, [pageType]);

  const addConditionText = useTranslation('add_condition');

  return (
    <Wrapper>
      <Container $active={isFilterDrawerVisible && !readOnly}>
        <Header>
          <FormattedMessage id={`${translationPrefix}_conditions_header`} />
        </Header>

        {!hasSomeValidFilter ? (
          <>
            <AddCondition
              onClick={openFilterDrawer}
              isEdit={readOnly}
              $readOnly={readOnly}
            >
              <FormattedMessage id={`${translationPrefix}_add_conditions`} />
            </AddCondition>
          </>
        ) : (
          <div>
            <FilterWrapper>
              <FilterAsBadge filter={filter} />
            </FilterWrapper>
            {!readOnly && (
              <AddCondition
                isEdit={true}
                onClick={openFilterDrawer}
                $readOnly={false}
              >
                <FormattedMessage id={`${translationPrefix}_edit_conditions`} />
              </AddCondition>
            )}
          </div>
        )}
        {!readOnly && (
          <GroupedFilterDrawer
            title={
              <div>
                <Title>
                  <FontAwesomeIcon icon={faFilter} />
                  <FormattedMessage id="filters" />
                </Title>
                <TitleDescription>
                  <FormattedMessage id="pages.filter_description" />
                </TitleDescription>
              </div>
            }
            hideRemoveButton={true}
            fields={groupedFields}
            filter={filter}
            onChange={onDone}
            visible={isFilterDrawerVisible}
            onClose={closeFilterDrawer}
            hasAiQuery={hasAiQuery}
            pageType={pageType}
            groupedTransformer={groupedTransformer}
            addButtonText={addConditionText}
          />
        )}
      </Container>
    </Wrapper>
  );
};
